// Modules
import { useRef, useEffect } from "react"
import styled from "styled-components"
import useFileUpload from "react-use-file-upload"
import { ValidationRules } from "react-hook-form"

// Constants
import colors from "~constants/colors"

// Components
import UploadIcon from "~images/icons/upload.svg?react"
import FiletypeCSVIcon from "~images/icons/filetype-csv.svg?react"
import { ButtonPrimarySoftLarge } from "~styled/button"

interface FileUploadProps {
  name: string
  accept?: string
  disabled?: boolean
  required?: boolean
  register: (fields: any, rules?: ValidationRules) => void
  setValue: (field: string, val: any) => void
  unregister: (field: string) => void
  text?: string
}

const FileUpload = (props: FileUploadProps) => {
  const {
    name,
    accept = "",
    disabled = false,
    required = false,
    register,
    setValue,
    unregister,
    text = "Drag a CSV file here or upload via the Import CSV file button",
  } = props

  const { files, setFiles, fileNames, removeFile, handleDragDropEvent } =
    useFileUpload()

  useEffect(() => {
    register(name, { required })
    return () => unregister(name)
  }, [name, register, unregister])

  useEffect(() => {
    setValue(name, files?.[0])
  }, [files])

  const inputRef: any = useRef()

  const onImportCSVFileButtonClick = event => {
    event.preventDefault()
    inputRef.current?.click()
  }

  const onDropHandler = event => {
    handleDragDropEvent(event)
    setFiles(event)
  }

  return (
    <FileUploadContainer>
      {/* DROPDOWN ZONE */}
      {!files.length && (
        <DropdownArea
          onDrop={e => onDropHandler(e)}
          onDragOver={(e: any) => handleDragDropEvent(e)}
          onDragEnter={(e: any) => handleDragDropEvent(e)}
          className={disabled ? "disabled" : ""}
        >
          <UploadIcon />
          <span>{text}</span>
        </DropdownArea>
      )}

      {/* FILE LIST */}
      {!!files.length && (
        <FileList>
          {fileNames.map(name => (
            <FileListItem key={name}>
              <FileListItemInfo>
                <FiletypeCSVIcon />

                <div className="data">{name}</div>
              </FileListItemInfo>

              <FileListItemDelete onClick={() => removeFile(name)}>
                Delete
              </FileListItemDelete>
            </FileListItem>
          ))}
        </FileList>
      )}

      {/* UPLOAD BUTTON */}
      <div style={{ textAlign: "center" }}>
        <ButtonPrimarySoftLarge
          style={{ height: 38 }}
          onClick={event => onImportCSVFileButtonClick(event)}
          disabled={disabled}
        >
          Import file
        </ButtonPrimarySoftLarge>

        <input
          ref={inputRef}
          type="file"
          accept={accept}
          style={{ display: "none" }}
          onChange={(e: any) => setFiles(e)}
        />
      </div>
    </FileUploadContainer>
  )
}

export default FileUpload

// -------------------- STYLED COMPONENTS --------------------
// -----------------------------------------------------------
const FileUploadContainer = styled.div``

const FileList = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
`

const FileListItem = styled.li`
  align-items: center;
  background-color: ${colors.gray1};
  border-radius: 8px;
  display: flex;
  height: 52px;
  justify-content: space-between;
  margin: 0 0 10px 0;
  padding: 0 10px;
`

const FileListItemInfo = styled.span`
  align-items: center;
  display: flex;

  > .data {
    line-height: 100%;
    margin-left: 10px;
  }
`

const FileListItemDelete = styled.span`
  background-color: ${colors.red2};
  border-radius: 8px;
  color: ${colors.red3};
  cursor: pointer;
  line-height: 32px;
  padding: 0 20px;
`

const DropdownArea = styled.div`
  align-items: center;
  border: 1px dashed rgba(0, 0, 0, 0.85);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  height: 150px;
  justify-content: center;
  margin-bottom: 15px;

  &.disabled {
    opacity: 0.5;
  }
`
